/**
 * CreateBriefModal Component
 *
 * Modal for creating or editing a brief.
 * Drive URLs are automatically inferred from client resources.
 */

import React, { useState, useEffect } from "react";
import { Modal } from "@/components/ui/Modal";
import { Brief, CreateBriefRequest, UpdateBriefRequest } from "../types";
import { ENTITY_CONFIG } from "@/core/entityConfig";

interface CreateBriefModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateBriefRequest | UpdateBriefRequest) => void;
  brief?: Brief | null;
  isLoading?: boolean;
}

export const CreateBriefModal: React.FC<CreateBriefModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  brief,
  isLoading = false,
}) => {
  const [title, setTitle] = useState("");

  const isEditing = !!brief;

  useEffect(() => {
    if (brief) {
      setTitle(brief.title || "");
    } else {
      setTitle("");
    }
  }, [brief, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data: CreateBriefRequest | UpdateBriefRequest = {
      title: title.trim() || undefined,
    };

    onSubmit(data);
  };

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        disabled={isLoading}
      >
        Cancel
      </button>
      <button
        type="submit"
        form="create-brief-form"
        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
        disabled={isLoading}
      >
        {isLoading
          ? "Saving..."
          : isEditing
            ? "Save Changes"
            : ENTITY_CONFIG.brief.createLabel}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? "Edit Brief" : ENTITY_CONFIG.brief.createLabel}
      size="md"
      footer={footer}
    >
      <form id="create-brief-form" onSubmit={handleSubmit} className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter brief title (optional)"
          />
        </div>
      </form>
    </Modal>
  );
};
